import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMe } from '../hooks/useMe'
import { useGlows } from '../hooks/useGlows'
import { useBillingPlans } from '../hooks/useBillingPlans'
import {
  useAmpPacks,
  useChangePlan,
  useCheckoutSession,
  usePortalSession,
  usePreviewAmp,
  usePreviewPlan,
  useRefreshMe,
  useResumeSubscription,
  useUpdateAmp,
} from '../hooks/useBilling'
import { Card } from '../components/ui/Card'
import { Modal } from '../components/ui/Modal'
import { Spinner } from '../components/ui/Spinner'
import { Button } from '../components/ui/Button'
import { SegmentedControl } from '../components/ui/SegmentedControl'
import { ErrorBanner } from '../components/ErrorBanner'
import { Badge } from '../components/Badge'
import { ApiError, apiErrorMessage } from '../api/client'
import { formatBytes, formatCount, formatCurrency } from '../lib/format'
import type {
  AmpItem,
  AmpPack,
  AmpPreview,
  BillingInterval,
  BillingPlan,
  PlanPreview,
  PlanPrice,
  Subscription,
} from '../api/types'

// The toggle's two options. Wire values are Stripe-verbatim month/year;
// "Monthly"/"Yearly" are presentation only.
const INTERVAL_OPTIONS: ReadonlyArray<{ value: BillingInterval; label: string }> = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
]

// intervalNoun renders the presentation word for a wire interval ("yearly
// billing", "move to monthly billing too").
function intervalNoun(interval: BillingInterval): 'monthly' | 'yearly' {
  return interval === 'year' ? 'yearly' : 'monthly'
}

export function Billing() {
  const me = useMe()
  const plans = useBillingPlans()
  // Glow list is loaded once here (not per plan card) so the pre-downgrade
  // warning can derive the lit-count CLIENT-SIDE (D8 — no /v1/me field). A
  // still-loading list yields litCount 0, which suppresses the warning (it
  // only fires when litCount > targetMax), so a slow/failed load degrades to
  // "no warning" rather than a wrong one.
  const glows = useGlows()
  const litCount = (glows.data ?? []).filter((g) => g.status === 'Lit').length
  const [searchParams, setSearchParams] = useSearchParams()
  const refreshMe = useRefreshMe()
  const [toast, setToast] = useState<string | null>(null)

  const subStatus = me.data?.subscription.status
  const isActive = subStatus === 'active' || subStatus === 'trialing'
  // Attached Amp packs are loaded once here (react-query caches by key): the
  // Amp section joins quantities from it, and the plan-switch dialog notes
  // the pack migration when an interval change has packs attached.
  const amp = useAmpPacks(isActive)

  // Monthly/yearly toggle. Seeded from the active sub's interval (else
  // monthly), and RE-seeded whenever subscription.interval changes (the
  // post-checkout / post-switch /v1/me refresh) — unless the user has
  // manually toggled since mount, in which case their choice wins.
  const [interval, setInterval] = useState<BillingInterval>('month')
  const userToggled = useRef(false)
  const subInterval = me.data?.subscription.interval ?? ''
  useEffect(() => {
    if (userToggled.current) return
    if (subInterval === 'month' || subInterval === 'year') setInterval(subInterval)
  }, [subInterval])

  // After returning from Stripe Checkout, refresh /v1/me so the flags
  // flip without a manual refresh. We then strip the query param so a
  // subsequent reload doesn't re-show the toast.
  useEffect(() => {
    const status = searchParams.get('status')
    if (status === 'success') {
      refreshMe()
      setToast('Thanks! Your subscription is active.')
      const next = new URLSearchParams(searchParams)
      next.delete('status')
      setSearchParams(next, { replace: true })
    } else if (status === 'cancelled') {
      setToast('Checkout cancelled — no charges were made.')
      const next = new URLSearchParams(searchParams)
      next.delete('status')
      setSearchParams(next, { replace: true })
    }
  }, [searchParams, setSearchParams, refreshMe])

  // While either /v1/me or the catalog is still loading, hold on a spinner.
  if (me.isLoading || plans.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  // /v1/me is required to render anything meaningful (current plan + status).
  if (me.error || !me.data) {
    return <ErrorBanner>{me.error ? (me.error as Error).message : 'Could not load profile.'}</ErrorBanner>
  }

  const sub = me.data.subscription
  const isPastDue = sub.status === 'past_due' || sub.status === 'canceled'
  const hasAmpPacks = (amp.data?.items.length ?? 0) > 0

  const onIntervalChange = (v: BillingInterval) => {
    userToggled.current = true
    setInterval(v)
  }

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div>
          <h1 className="mb-1 text-2xl font-semibold">Billing</h1>
          <p className="text-sm text-cream/70">
            Choose a plan to unlock paid features across every glow you own.
          </p>
        </div>
        {/* Billing history lives in the Stripe portal (invoices + receipts).
            Shown once the owner has a REAL billing relationship — a customer
            plus a non-placeholder status (active/past_due/canceled/…), not the
            free Spark mirror (status 'none'), which has no invoices. */}
        {sub.has_billing_customer && sub.status !== 'none' && <BillingHistoryButton />}
      </div>

      {toast && (
        <div className="mb-4 rounded-md border border-green-500/25 bg-green-500/10 px-3 py-2 text-sm text-green-300">
          {toast}
        </div>
      )}

      {isPastDue && <PastDueBanner sub={sub} />}
      {sub.cancel_at_period_end && <ScheduledDowngradeBanner />}

      {/* The catalog drives the grid. If it failed to load we can still show
          the page chrome + past-due recovery, but not the plan cards. */}
      {plans.error || !plans.data ? (
        <ErrorBanner>
          {plans.error ? (plans.error as Error).message : 'Could not load plans.'}
        </ErrorBanner>
      ) : (
        <>
          <div className="mb-4">
            <SegmentedControl
              options={INTERVAL_OPTIONS}
              value={interval}
              onChange={onIntervalChange}
              aria-label="Billing interval"
            />
          </div>
          <PlanGrid
            plans={plans.data.plans}
            sub={sub}
            interval={interval}
            isActive={isActive}
            hasAmpPacks={hasAmpPacks}
            litCount={litCount}
            sparkMax={plans.data.plans.find((p) => p.plan === 'spark')?.quotas.max_glows ?? 0}
          />
          <AmpSection
            packs={plans.data.amp_packs}
            isActive={isActive}
            subInterval={sub.interval}
            attached={amp.data?.items ?? []}
          />
        </>
      )}

      <ArcCard />

      <section id="contact" className="mt-10 border-t border-line pt-6">
        <h2 className="text-base font-medium text-cream">Contact us</h2>
        <p className="mt-1 text-sm text-cream/70">
          Reach out via your account manager or the in-app support channel and
          we&apos;ll set up an Arc plan tailored to your team.
        </p>
      </section>
    </div>
  )
}

// BillingHistoryButton opens the Stripe Customer Portal, where invoices +
// receipts live. Shown only when a Stripe Customer exists (has_billing_customer).
function BillingHistoryButton() {
  const portal = usePortalSession()
  const [error, setError] = useState<string | null>(null)

  const open = async () => {
    setError(null)
    try {
      const res = await portal.mutateAsync({ return_url: `${window.location.origin}/billing` })
      window.location.href = res.url
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_billing_customer') {
        setError('No billing history yet.')
        return
      }
      setError(apiErrorMessage(e, 'Could not open billing history.'))
    }
  }

  return (
    <div className="shrink-0 text-right">
      <Button variant="secondary" disabled={portal.isPending} onClick={open}>
        {portal.isPending ? 'Opening…' : 'View Billing History'}
      </Button>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  )
}

// ScheduledDowngradeBanner appears while a downgrade to Spark is scheduled
// (cancel_at_period_end). "Keep my plan" resumes the subscription.
function ScheduledDowngradeBanner() {
  const resume = useResumeSubscription()
  const refreshMe = useRefreshMe()
  const [error, setError] = useState<string | null>(null)

  const onResume = async () => {
    setError(null)
    try {
      await resume.mutateAsync()
      refreshMe()
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not resume your plan.'))
    }
  }

  return (
    <Card className="mb-6 border-amber-500/30">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-medium text-cream">Scheduled downgrade</h2>
            <Badge tone="amber">Pending</Badge>
          </div>
          <p className="mt-1 text-sm text-cream/70">
            Your plan switches to Spark (free) at the end of your current billing
            period. You keep your paid features until then.
          </p>
          {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
        </div>
        <Button
          variant="secondary"
          disabled={resume.isPending}
          className="shrink-0"
          onClick={onResume}
        >
          {resume.isPending ? '…' : 'Keep my plan'}
        </Button>
      </div>
    </Card>
  )
}

// formatQuotaBytes renders a quota byte count, treating 0 as "Unlimited"
// (the cap convention shared with Meter / UsageTab).
function formatQuotaBytes(bytes: number): string {
  return bytes === 0 ? 'Unlimited' : formatBytes(bytes)
}

function formatQuotaCount(n: number): string {
  return n === 0 ? 'Unlimited' : formatCount(n)
}

// ---------------------------------------------------------------------------
// Past-due recovery banner
// ---------------------------------------------------------------------------

function PastDueBanner({ sub }: { sub: Subscription }) {
  const portal = usePortalSession()
  const [error, setError] = useState<string | null>(null)

  const openPortal = async () => {
    setError(null)
    try {
      const res = await portal.mutateAsync({ return_url: `${window.location.origin}/billing` })
      window.location.href = res.url
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_billing_customer') {
        setError('No Stripe customer yet. Subscribe first to set up billing.')
        return
      }
      setError(apiErrorMessage(e, 'Could not open billing portal.'))
    }
  }

  return (
    <Card className="mb-6 border-red-500/30">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-lg font-semibold">Payment needed</h2>
        <Badge tone="red">{sub.status}</Badge>
      </div>
      <p className="text-sm text-cream/70">
        Your subscription is{' '}
        {sub.status === 'past_due' ? 'past due' : 'cancelled'}. Update your
        payment method or resubscribe to restore paid features.
      </p>
      {error && <ErrorBanner className="mt-4">{error}</ErrorBanner>}
      <div className="mt-5">
        <Button onClick={openPortal} disabled={portal.isPending}>
          {portal.isPending ? 'Opening…' : 'Update payment / resubscribe'}
        </Button>
      </div>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Plan grid
// ---------------------------------------------------------------------------

function PlanGrid({
  plans,
  sub,
  interval,
  isActive,
  hasAmpPacks,
  litCount,
  sparkMax,
}: {
  plans: BillingPlan[]
  sub: Subscription
  // The toggle's selected billing interval — drives each card's price,
  // current-plan state, and the bodies of checkout/switch calls.
  interval: BillingInterval
  isActive: boolean
  hasAmpPacks: boolean
  // Lit-glow count + Spark's glow limit, both derived once in Billing() and
  // threaded down so each card's confirm dialog can warn how many glows a
  // downgrade/cancel will darken (Spark's limit on the cancel path).
  litCount: number
  sparkMax: number
}) {
  // A custom subscriber gets a 4th current-plan panel appended after the
  // catalog cards (client-side — deliberately OUTSIDE the generated
  // PLAN_ORDER, which only carries the canonical base tiers).
  return (
    <div
      className={`grid gap-4 sm:grid-cols-2 ${sub.is_custom ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}
    >
      {plans.map((p) => (
        <PlanCard
          key={p.plan}
          plan={p}
          sub={sub}
          interval={interval}
          isActive={isActive}
          hasAmpPacks={hasAmpPacks}
          litCount={litCount}
          sparkMax={sparkMax}
        />
      ))}
      {sub.is_custom && <CustomPlanCard sub={sub} />}
    </div>
  )
}

function PlanCard({
  plan,
  sub,
  interval,
  isActive,
  hasAmpPacks,
  litCount,
  sparkMax,
}: {
  plan: BillingPlan
  sub: Subscription
  interval: BillingInterval
  isActive: boolean
  hasAmpPacks: boolean
  litCount: number
  sparkMax: number
}) {
  const checkout = useCheckoutSession()
  const previewPlan = usePreviewPlan()
  const changePlan = useChangePlan()
  const refreshMe = useRefreshMe()
  const [error, setError] = useState<string | null>(null)
  // The pending plan change awaiting confirmation (the priced quote).
  const [pending, setPending] = useState<{ quote: PlanPreview } | null>(null)

  const isSpark = plan.plan === 'spark'
  // A custom subscriber's slug matches no card — and even a tier-tagged
  // custom price must not light a card up, so is_custom wins outright.
  const planMatches = !sub.is_custom && plan.plan === sub.plan
  // "Current plan" ⇔ plan match AND toggle == sub interval; Spark is
  // interval-agnostic (free has no billing interval).
  const isCurrent = planMatches && (isSpark || interval === sub.interval)
  // The current plan's card under the OTHER toggle position offers a
  // billing-interval switch — a real "switch" server-side, since noop is
  // decided by price-id equality.
  const isIntervalSwitch = planMatches && !isSpark && isActive && !isCurrent
  // The selected interval's price entry. A missing key / empty price_id means
  // "not purchasable at this interval" (Spark, or an unconfigured plan).
  const price = plan.prices[interval]
  const hasPrice = (price?.price_id ?? '') !== ''
  const priceLabel = price?.price_label ?? '—'
  // Whether a confirmed switch crosses billing intervals (drives the dialog's
  // anchor-reset copy + the Amp-migration note).
  const intervalChange = !isSpark && sub.interval !== '' && interval !== sub.interval

  // Non-active (free / lapsed) owner subscribing to a paid plan — the initial
  // flow stays on Stripe Checkout.
  const startCheckout = async () => {
    const target = plan.plan
    if (target === 'spark') return // free — there is no checkout path
    setError(null)
    try {
      const origin = window.location.origin
      const res = await checkout.mutateAsync({
        plan: target,
        interval,
        success_url: `${origin}/billing?status=success`,
        cancel_url: `${origin}/billing?status=cancelled`,
      })
      window.location.href = res.url
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not start checkout.'))
    }
  }

  // Active subscriber switching plans in-app: price the change first (so the
  // dialog shows the exact figure), then open the confirm dialog. Nothing is
  // billed until they confirm. 409 amp_interval_unavailable = an attached
  // pack has no sibling at the target interval — the owner removes it first.
  const requestSwitch = async () => {
    setError(null)
    try {
      const quote = await previewPlan.mutateAsync({ plan: plan.plan, interval })
      if (quote.action === 'noop') return
      setPending({ quote })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'amp_interval_unavailable') {
        setError(
          'An attached Amp storage pack is not available at that billing interval — remove it first, then switch.',
        )
        return
      }
      setError(apiErrorMessage(e, 'Could not price that change.'))
    }
  }

  const confirmSwitch = async () => {
    if (!pending) return
    setError(null)
    try {
      await changePlan.mutateAsync({
        plan: plan.plan,
        interval,
        proration_date: pending.quote.proration_date,
      })
      setPending(null)
      refreshMe()
    } catch (e) {
      setPending(null)
      if (e instanceof ApiError && e.code === 'amp_interval_unavailable') {
        setError(
          'An attached Amp storage pack is not available at that billing interval — remove it first, then switch.',
        )
        return
      }
      setError(apiErrorMessage(e, 'Could not change your plan.'))
    }
  }

  const busy = checkout.isPending || previewPlan.isPending || changePlan.isPending

  return (
    <>
      <Card className={isCurrent ? 'border-glow-500/50 ring-1 ring-glow-500/30' : ''}>
        <div className="mb-1 flex items-center gap-2">
          <h2 className="text-lg font-semibold">{plan.display_name}</h2>
          {isCurrent && <Badge tone="amber">Current</Badge>}
        </div>
        <p className="text-sm text-cream/70">{priceLabel}</p>

        {plan.features.length > 0 && (
          <ul className="mt-3 space-y-1.5 text-sm">
            {plan.features.map((f) => (
              <li key={f} className="flex items-start gap-2">
                <span className="text-green-600" aria-hidden>
                  ✓
                </span>
                <span className="text-cream/80">{f}</span>
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-4 space-y-1 border-t border-line pt-3 text-xs">
          <QuotaRow label="Storage" value={formatQuotaBytes(plan.quotas.storage_bytes)} />
          <QuotaRow label="Bandwidth" value={formatQuotaBytes(plan.quotas.egress_bytes)} />
          <QuotaRow label="Requests" value={`${formatQuotaCount(plan.quotas.requests)}/mo`} />
          <QuotaRow
            label="Glows"
            value={plan.quotas.max_glows === 0 ? 'Unlimited' : String(plan.quotas.max_glows)}
          />
          <QuotaRow label="History" value={plan.quotas.retention} />
        </dl>

        {error && <ErrorBanner className="mt-4">{error}</ErrorBanner>}

        <div className="mt-5">
          <PlanAction
            isCurrent={isCurrent}
            isIntervalSwitch={isIntervalSwitch}
            isSpark={isSpark}
            isActive={isActive}
            hasPrice={hasPrice}
            displayName={plan.display_name}
            intervalLabel={intervalNoun(interval)}
            busy={busy}
            onCheckout={startCheckout}
            onSwitch={requestSwitch}
          />
        </div>
      </Card>
      {pending && (
        <ConfirmPlanDialog
          displayName={plan.display_name}
          priceLabel={priceLabel}
          quote={pending.quote}
          intervalOnly={isIntervalSwitch}
          intervalChange={intervalChange}
          intervalLabel={intervalNoun(interval)}
          hasAmpPacks={hasAmpPacks}
          fromCustom={sub.is_custom}
          litCount={litCount}
          // The lit cap the owner is downgrading INTO: a paid↔paid switch lands
          // on the clicked card's plan, so its max_glows; a cancel downgrades to
          // Spark, so Spark's max_glows (NOT the clicked card's).
          targetMax={pending.quote.action === 'cancel' ? sparkMax : plan.quotas.max_glows}
          busy={changePlan.isPending}
          onConfirm={confirmSwitch}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  )
}

function QuotaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="min-w-0 text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-right font-mono tabular-nums text-cream/80">{value}</dd>
    </div>
  )
}

// PlanAction is the single button on each plan card. The action is a pure
// function of (isCurrent × isIntervalSwitch × isActive × isSpark × hasPrice):
//   - current plan + interval → disabled "Current plan"
//   - current plan, OTHER toggle interval (active) → "Switch to yearly/monthly
//     billing" (a real switch — same preview/confirm flow)
//   - active paid user, other → in-app "Switch to X" / "Downgrade to Spark"
//     (paid↔paid price swap, or cancel-at-period-end; via the confirm dialog)
//   - non-active user, Spark  → disabled "Current plan" (free is the
//     default tier for a non-subscriber)
//   - non-active user, paid   → "Subscribe" via Checkout (the initial flow),
//     disabled when the selected interval's price isn't configured yet
function PlanAction({
  isCurrent,
  isIntervalSwitch,
  isSpark,
  isActive,
  hasPrice,
  displayName,
  intervalLabel,
  busy,
  onCheckout,
  onSwitch,
}: {
  isCurrent: boolean
  isIntervalSwitch: boolean
  isSpark: boolean
  isActive: boolean
  hasPrice: boolean
  displayName: string
  intervalLabel: 'monthly' | 'yearly'
  busy: boolean
  onCheckout: () => void
  onSwitch: () => void
}) {
  if (isCurrent) {
    return (
      <Button variant="secondary" disabled className="w-full">
        Current plan
      </Button>
    )
  }

  // Active paid user → in-app plan change (price/interval swap, or
  // downgrade-to-Spark).
  if (isActive) {
    if (!isSpark && !hasPrice) {
      return (
        <Button disabled className="w-full" title="Not available yet">
          Not available yet
        </Button>
      )
    }
    if (isSpark) {
      return (
        <Button variant="secondary" disabled={busy} className="w-full" onClick={onSwitch}>
          {busy ? '…' : 'Downgrade to Spark'}
        </Button>
      )
    }
    return (
      <Button disabled={busy} className="w-full" onClick={onSwitch}>
        {busy
          ? '…'
          : isIntervalSwitch
            ? `Switch to ${intervalLabel} billing`
            : `Switch to ${displayName}`}
      </Button>
    )
  }

  // Non-active (Spark / no subscription). Spark is already their effective
  // plan; only paid cards offer Checkout.
  if (isSpark) {
    return (
      <Button variant="secondary" disabled className="w-full">
        Current plan
      </Button>
    )
  }

  if (!hasPrice) {
    return (
      <Button disabled className="w-full" title="Not available yet">
        Not available yet
      </Button>
    )
  }

  return (
    <Button disabled={busy} className="w-full" onClick={onCheckout}>
      {busy ? 'Loading…' : 'Subscribe'}
    </Button>
  )
}

// ConfirmPlanDialog shows the exact effect of an in-app plan change before it's
// applied: a "switch" charges/credits a prorated amount immediately; a "cancel"
// schedules a downgrade to Spark at period end (no charge now). On an interval
// change the quoted amount is the TOTAL immediate invoice (proration credits +
// the full first new-interval period — the billing anchor resets), so the copy
// says the new billing period starts today.
function ConfirmPlanDialog({
  displayName,
  priceLabel,
  quote,
  intervalOnly,
  intervalChange,
  intervalLabel,
  hasAmpPacks,
  fromCustom,
  litCount,
  targetMax,
  busy,
  onConfirm,
  onCancel,
}: {
  displayName: string
  priceLabel: string
  quote: PlanPreview
  // intervalOnly = same plan, other interval (titles the dialog as a billing-
  // interval switch); intervalChange = the switch crosses intervals (anchor
  // reset + Amp migration), which also holds for cross-plan interval changes.
  intervalOnly: boolean
  intervalChange: boolean
  intervalLabel: 'monthly' | 'yearly'
  // Whether any Amp packs are attached — an interval change migrates them too.
  hasAmpPacks: boolean
  // Switching away from a custom plan is one-way (it has no picker card).
  fromCustom: boolean
  // litCount = the owner's currently-lit glows; targetMax = the glow limit of
  // the plan they're downgrading INTO (the clicked card's, or Spark's on a
  // cancel). 0 = unlimited → no warning. When litCount > targetMax the dialog
  // warns how many glows the downgrade will auto-darken (oldest-lit-first).
  litCount: number
  targetMax: number
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const cancel = quote.action === 'cancel'
  const charge = quote.action === 'switch' && quote.amount_due > 0
  const credit = quote.action === 'switch' && quote.amount_due < 0
  const crossesInterval = quote.action === 'switch' && intervalChange
  const money = formatCurrency(Math.abs(quote.amount_due), quote.currency)
  const willDarken = targetMax > 0 && litCount > targetMax
  const darkenCount = litCount - targetMax

  return (
    <Modal onClose={onCancel} size="md" labelledBy="confirm-plan-title">
      <h3 id="confirm-plan-title" className="text-lg font-semibold">
        {cancel
          ? 'Downgrade to Spark?'
          : intervalOnly
            ? `Switch to ${intervalLabel} billing?`
            : `Switch to ${displayName}?`}
      </h3>
        <p className="mt-2 text-sm text-cream/70">
          {cancel
            ? 'Your plan stays active until the end of your current billing period, then switches to Spark (free). No charge now — you keep your paid features until then.'
            : intervalOnly
              ? `You'll stay on ${displayName}, billed ${intervalLabel} (${priceLabel}).`
              : `You'll move to ${displayName} (${priceLabel}).`}
        </p>
        {fromCustom && (
          <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-300">
            You&apos;re leaving your custom plan — you won&apos;t be able to
            switch back to it from here.
          </div>
        )}
        {willDarken && (
          <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {darkenCount} of your {litCount} lit glows will be darkened — only
            the {targetMax} most recent stay lit. You can relight them after
            upgrading again.
          </div>
        )}
        {!cancel && (
          <p className="mt-3 text-sm text-cream/80">
            {charge && !crossesInterval && (
              <>
                You&apos;ll be charged{' '}
                <span className="font-semibold text-cream">{money}</span> now,
                prorated for the rest of your current billing period.
              </>
            )}
            {charge && crossesInterval && (
              <>
                You&apos;ll be charged{' '}
                <span className="font-semibold text-cream">{money}</span> now —
                unused time on your current period is credited toward it — and
                your new billing period starts today.
              </>
            )}
            {credit && (
              <>
                You&apos;ll receive a{' '}
                <span className="font-semibold text-cream">{money}</span> credit
                for the unused time, applied to your account.
                {crossesInterval && <> Your new billing period starts today.</>}
              </>
            )}
            {quote.amount_due === 0 && <>No charge applies right now.</>}
          </p>
        )}
        {crossesInterval && hasAmpPacks && (
          <p className="mt-2 text-sm text-cream/80">
            Your Amp storage packs move to {intervalLabel} billing too.
          </p>
        )}
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button className="w-full sm:w-auto" onClick={onConfirm} disabled={busy}>
            {busy
              ? 'Processing…'
              : cancel
                ? 'Confirm downgrade'
                : charge
                  ? `Pay ${money}`
                  : 'Confirm'}
          </Button>
        </div>
    </Modal>
  )
}

// CustomPlanCard is the 4th panel a custom subscriber sees (is_custom from
// /v1/me): their hand-authored base price is not a catalog card, so the picker
// can't highlight one. Quotas render from the resolved account caps /v1/me
// already carries; retention is the static paid 30d window. No action button —
// a custom plan is managed out of band, and switching away goes through the
// normal cards (with a one-way warning). Renders identically under both
// toggle positions (a custom price's interval is whatever was hand-authored).
function CustomPlanCard({ sub }: { sub: Subscription }) {
  return (
    <Card className="border-glow-500/50 ring-1 ring-glow-500/30">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-lg font-semibold">{sub.plan_label || 'Custom Plan'}</h2>
        <Badge tone="amber">Current</Badge>
      </div>
      <p className="text-sm text-cream/70">Custom pricing</p>

      <dl className="mt-4 space-y-1 border-t border-line pt-3 text-xs">
        <QuotaRow label="Storage" value={formatQuotaBytes(sub.storage_cap_bytes)} />
        <QuotaRow label="Bandwidth" value={formatQuotaBytes(sub.egress_cap_bytes)} />
        <QuotaRow label="Requests" value={`${formatQuotaCount(sub.requests_cap)}/mo`} />
        <QuotaRow
          label="Glows"
          value={sub.max_glows === 0 ? 'Unlimited' : String(sub.max_glows)}
        />
        <QuotaRow label="History" value="30d" />
      </dl>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Amp packs (storage add-ons)
// ---------------------------------------------------------------------------

function AmpSection({
  packs,
  isActive,
  subInterval,
  attached,
}: {
  packs: AmpPack[]
  isActive: boolean
  subInterval: Subscription['interval']
  // The packs currently attached to the active subscription (loaded once in
  // Billing()).
  attached: AmpItem[]
}) {
  // Amp packs are a paid add-on stacked on an active plan — hide the whole
  // section unless the owner is on an active paid plan. You can't add storage
  // to Spark (free) or a canceled account; the data API rejects it server-side
  // too (404 no_active_subscription).
  if (!isActive || packs.length === 0) return null

  // Amp packs must bill on the SUBSCRIPTION's interval (Stripe forbids mixing;
  // the server 400s amp_interval_mismatch), so the section follows the sub's
  // interval — never the toggle's — and only offers packs purchasable there.
  const ampInterval: BillingInterval = subInterval === 'year' ? 'year' : 'month'
  const offered = packs.flatMap((pack) => {
    const price = pack.prices[ampInterval]
    return price && price.price_id !== '' ? [{ pack, price }] : []
  })
  if (offered.length === 0) return null

  // Attached packs join by add_storage_bytes identity, not price id — a
  // custom-priced pack of the same size still matches its catalog card.
  const quantityFor = (addStorageBytes: number) =>
    attached.find((i) => i.add_storage_bytes === addStorageBytes)?.quantity ?? 0

  return (
    <div className="mt-8">
      <h2 className="text-lg font-semibold">Amp — extra storage</h2>
      <p className="mt-1 text-sm text-cream/70">
        Stackable storage add-ons for an active paid plan. Add or remove anytime
        — your storage limit updates shortly after the change.
      </p>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {offered.map(({ pack, price }) => (
          <AmpCard
            key={pack.display_name}
            pack={pack}
            price={price}
            isActive={isActive}
            quantity={quantityFor(pack.add_storage_bytes)}
          />
        ))}
      </div>
    </div>
  )
}

function AmpCard({
  pack,
  price,
  isActive,
  quantity,
}: {
  pack: AmpPack
  // The pack's price at the SUBSCRIPTION's interval (AmpSection already
  // filtered to it) — its price_id is what the mutations send.
  price: PlanPrice
  isActive: boolean
  quantity: number
}) {
  const preview = usePreviewAmp()
  const update = useUpdateAmp()
  const [error, setError] = useState<string | null>(null)
  // The change awaiting confirmation: the target quantity + the priced quote.
  const [pending, setPending] = useState<{ targetQty: number; quote: AmpPreview } | null>(null)

  // requestChange prices the change first (so the dialog can show the exact
  // figure), then opens the confirm dialog. Nothing is billed yet.
  const requestChange = async (targetQty: number) => {
    setError(null)
    try {
      const quote = await preview.mutateAsync({ price_id: price.price_id, quantity: targetQty })
      setPending({ targetQty, quote })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'no_active_subscription') {
        setError('Subscribe to a paid plan before adding Amp packs.')
        return
      }
      setError(apiErrorMessage(e, 'Could not price that change.'))
    }
  }

  // confirmChange applies the change, passing the quote's proration_date so the
  // charge matches the figure the user just saw.
  const confirmChange = async () => {
    if (!pending) return
    setError(null)
    try {
      await update.mutateAsync({
        price_id: price.price_id,
        quantity: pending.targetQty,
        proration_date: pending.quote.proration_date,
      })
      setPending(null)
    } catch (e) {
      setPending(null)
      setError(apiErrorMessage(e, 'Could not update your storage packs.'))
    }
  }

  const attached = quantity > 0
  const busy = preview.isPending || update.isPending

  return (
    <>
      <Card>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold">{pack.display_name}</h3>
            <p className="text-sm text-cream/70">{price.price_label}</p>
            <p className="mt-1 text-xs text-muted">
              {formatBytes(pack.add_storage_bytes)} added storage
            </p>
          </div>
          {attached && <Badge tone="amber">×{quantity}</Badge>}
        </div>
        {error && <ErrorBanner className="mt-3">{error}</ErrorBanner>}
        <div className="mt-4">
          {!isActive ? (
            <Button
              variant="secondary"
              disabled
              className="w-full"
              title="Subscribe to a paid plan first"
            >
              Subscribe to a paid plan first
            </Button>
          ) : attached ? (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                disabled={busy}
                className="flex-1"
                onClick={() => requestChange(quantity - 1)}
              >
                {busy ? '…' : 'Remove'}
              </Button>
              <Button
                disabled={busy}
                className="flex-1"
                onClick={() => requestChange(quantity + 1)}
              >
                {busy ? '…' : 'Add another'}
              </Button>
            </div>
          ) : (
            <Button
              disabled={busy}
              className="w-full"
              onClick={() => requestChange(1)}
            >
              {busy ? 'Pricing…' : 'Add'}
            </Button>
          )}
        </div>
      </Card>
      {pending && (
        <ConfirmAmpDialog
          pack={pack}
          priceLabel={price.price_label}
          quote={pending.quote}
          isIncrease={pending.targetQty > quantity}
          busy={update.isPending}
          onConfirm={confirmChange}
          onCancel={() => setPending(null)}
        />
      )}
    </>
  )
}

// ConfirmAmpDialog shows the exact prorated amount (from the preview) before an
// Amp change is billed. A positive amount is an immediate charge; a negative
// amount is a credit (removing a pack mid-cycle).
function ConfirmAmpDialog({
  pack,
  priceLabel,
  quote,
  isIncrease,
  busy,
  onConfirm,
  onCancel,
}: {
  pack: AmpPack
  priceLabel: string
  quote: AmpPreview
  isIncrease: boolean
  busy: boolean
  onConfirm: () => void
  onCancel: () => void
}) {
  const charge = quote.amount_due > 0
  const credit = quote.amount_due < 0
  const money = formatCurrency(Math.abs(quote.amount_due), quote.currency)

  return (
    <Modal onClose={onCancel} size="md" labelledBy="confirm-amp-title">
      <h3 id="confirm-amp-title" className="text-lg font-semibold">
        {isIncrease ? 'Add' : 'Remove'} {pack.display_name}?
      </h3>
        <p className="mt-2 text-sm text-cream/70">
          {isIncrease
            ? `This adds ${formatBytes(pack.add_storage_bytes)} of storage to your account (${priceLabel}).`
            : `This removes ${formatBytes(pack.add_storage_bytes)} of storage from your account.`}
        </p>
        <p className="mt-3 text-sm text-cream/80">
          {charge && (
            <>
              You&apos;ll be charged{' '}
              <span className="font-semibold text-cream">{money}</span> now,
              prorated for the rest of your current billing period.
            </>
          )}
          {credit && (
            <>
              You&apos;ll receive a{' '}
              <span className="font-semibold text-cream">{money}</span> credit
              for the unused time on your account.
            </>
          )}
          {quote.amount_due === 0 && <>No charge applies right now.</>}
        </p>
        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="secondary"
            className="w-full sm:w-auto"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button className="w-full sm:w-auto" onClick={onConfirm} disabled={busy}>
            {busy ? 'Processing…' : charge ? `Pay ${money}` : 'Confirm'}
          </Button>
        </div>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Arc (enterprise) — static, no API call (D14)
// ---------------------------------------------------------------------------

function ArcCard() {
  const features = [
    'Team access & shared ownership',
    'SSO / SAML',
    'Custom limits & dedicated regions',
    'Dedicated support & SLAs',
  ]
  return (
    <Card className="mt-8">
      <div className="mb-1 flex items-center gap-2">
        <h2 className="text-lg font-semibold">Arc</h2>
        <Badge tone="slate">Enterprise</Badge>
      </div>
      <p className="text-sm text-cream/70">Talk to us — built for teams.</p>
      <ul className="mt-3 space-y-1.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span className="text-green-600" aria-hidden>
              ✓
            </span>
            <span className="text-cream/80">{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-5">
        <a
          href="#contact"
          className="font-medium text-glow-600 hover:underline"
        >
          Contact us
        </a>
      </div>
    </Card>
  )
}
